/**
 * WorkWell Central KPI Presentation Rules
 * Source: WorkWell Finance and Operations Domain Specification
 * Ownership: Finance Platform Architecture Team
 * 
 * Central configuration mapping metrics to business directions (higher-better vs. lower-better)
 * and thresholds. Evaluates visual status and trend directions from calculations.
 */

export interface MetricRule {
  key: string;
  direction: 'higher-better' | 'lower-better';
  healthyMin?: number;
  healthyMax?: number;
  warningMin?: number;
  warningMax?: number;
  criticalMin?: number;
  criticalMax?: number;
}

export const METRIC_RULES: Record<string, MetricRule> = {
  nfi: {
    key: 'nfi',
    direction: 'higher-better',
    healthyMin: 10000 // positive green if >= 10k
  },
  gross_profit_margin: {
    key: 'gross_profit_margin',
    direction: 'higher-better',
    healthyMin: 80 // positive green if >= 80%
  },
  ebitda: {
    key: 'ebitda',
    direction: 'higher-better',
    healthyMin: 5000 // positive green if >= 5k
  },
  ebitda_margin: {
    key: 'ebitda_margin',
    direction: 'higher-better',
    healthyMin: 70 // positive green if >= 70%
  },
  net_profit_margin: {
    key: 'net_profit_margin',
    direction: 'higher-better',
    healthyMin: 70 // positive green if >= 70%
  },
  debtor_days: {
    key: 'debtor_days',
    direction: 'lower-better',
    criticalMin: 45 // critical red if DSO > 45 days
  },
  cash_collected_pct: {
    key: 'cash_collected_pct',
    direction: 'higher-better',
    healthyMin: 90,
    warningMin: 75
  },
  revenue_concentration: {
    key: 'revenue_concentration',
    direction: 'lower-better',
    warningMin: 50 // warning amber if concentration > 50%
  },
  operating_cost_pct: {
    key: 'operating_cost_pct',
    direction: 'lower-better',
    warningMin: 70,
    criticalMin: 85
  },
  aged_debt_90_pct: {
    key: 'aged_debt_90_pct',
    direction: 'lower-better',
    warningMin: 15,
    criticalMin: 30
  }
};

/**
 * Evaluates current KPI calculated value against metric thresholds to yield status.
 */
export function evaluateKpiStatus(key: string, value: number | null): 'positive' | 'warning' | 'critical' | 'neutral' {
  if (value === null || value === undefined) return 'neutral';
  const rule = METRIC_RULES[key];
  if (!rule) return 'neutral';

  if (rule.direction === 'higher-better') {
    if (rule.criticalMax !== undefined && value <= rule.criticalMax) return 'critical';
    if (rule.criticalMin !== undefined && value < rule.criticalMin) return 'critical';
    if (rule.warningMin !== undefined && value < rule.warningMin) return 'warning';
    if (rule.healthyMin !== undefined && value >= rule.healthyMin) return 'positive';
    return 'neutral';
  } else {
    if (rule.criticalMin !== undefined && value > rule.criticalMin) return 'critical';
    if (rule.warningMin !== undefined && value > rule.warningMin) return 'warning';
    if (rule.healthyMax !== undefined && value <= rule.healthyMax) return 'positive';
    if (rule.healthyMin !== undefined && value <= rule.healthyMin) return 'positive';
    return 'neutral';
  }
}

/**
 * Derives trend direction only from actual current vs previous period comparison.
 */
export function evaluateKpiTrend(
  key: string,
  current: number | null,
  previous: number | null
): 'up' | 'down' | 'flat' | null {
  if (current === null || previous === null || current === undefined || previous === undefined) {
    return null;
  }
  const diff = current - previous;
  if (Math.abs(diff) < 0.001) return 'flat';
  return diff > 0 ? 'up' : 'down';
}
